/**
 * User-facing doctor copy keyed by OS + browser.
 * Only talk about the store this combination can actually create.
 */

export function normalizeOs(os) {
  const o = (os || '').toLowerCase();
  if (o.includes('ios') || o.includes('iphone') || o.includes('ipad')) return 'iOS';
  if (o.includes('mac')) return 'macOS';
  if (o.includes('win')) return 'Windows';
  if (o.includes('android')) return 'Android';
  if (o.includes('linux')) return 'Linux';
  return os || 'Unknown';
}

export function normalizeBrowser(browser) {
  const b = (browser || '').toLowerCase();
  if (b.includes('edg')) return 'Edge';
  if ((b.includes('chrome') || b.includes('chromium')) && !b.includes('edg')) return 'Chrome';
  if (b.includes('safari')) return 'Safari';
  if (b.includes('firefox')) return 'Firefox';
  return browser || 'Unknown';
}

export function platformKey(os, browser) {
  return `${normalizeOs(os)}:${normalizeBrowser(browser)}`;
}

export function audience(os, browser) {
  return `${normalizeBrowser(browser)} on ${normalizeOs(os)}`;
}

const COPY = {
  'macOS:Safari': {
    hero: 'Safari on this Mac stores platform passkeys in iCloud Keychain. Those passkeys are synced across your Apple devices.',
    intro: 'Safari will use iCloud Keychain (Apple Passwords). Touch ID only unlocks the key. There is no this-Mac-only discoverable passkey in Safari.',
    hint: 'The email is not stored. It only labels this Safari test passkey.',
    prompt: 'Complete the Safari / Touch ID or password prompt.',
    syncedResult: 'On Safari on this Mac, platform passkeys are stored in iCloud Keychain and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Safari stored the passkey in iCloud Keychain',
    storeFailSteps: [
      'Expected. iCloud Keychain is Safari’s platform passkey store on this Mac.',
      'Those passkeys are synced.',
      'A hardware security key is the non-syncing option in Safari.',
    ],
    noPlatformTitle: 'Safari cannot create a platform passkey yet',
    noPlatformSteps: [
      'Set a Mac login password (required).',
      'Turn on iCloud Keychain: System Settings → Apple ID → iCloud → Passwords.',
      'Retry in Safari after Keychain is on.',
    ],
  },
  'macOS:Chrome': {
    hero: 'Chrome on this Mac stores platform passkeys in iCloud Keychain or Google Password Manager. Both stores sync.',
    intro: 'Chrome on macOS uses iCloud Keychain (the OS provider) or Google Password Manager. Touch ID only unlocks the key. Both of those stores sync.',
    hint: 'The email is not stored. Chrome may show iCloud Keychain or Google Password Manager.',
    prompt: 'Complete the Chrome passkey prompt.',
    syncedResult: 'On Chrome on this Mac, platform passkeys are synced (iCloud Keychain or Google Password Manager).',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Chrome stored the passkey in a synced store',
    storeFailSteps: [
      'On this Mac, Chrome’s platform stores are iCloud Keychain or Google Password Manager.',
      'Both sync.',
      'A hardware security key is the non-syncing option.',
    ],
    noPlatformTitle: 'Chrome cannot see a platform authenticator',
    noPlatformSteps: [
      'Set a Mac login password.',
      'Enable iCloud Keychain if you want Apple’s store, or sign into Chrome for Google Password Manager.',
      'Retry in Chrome.',
    ],
  },
  'macOS:Edge': {
    hero: 'Edge on this Mac uses iCloud Keychain for platform passkeys. Those passkeys are synced.',
    intro: 'Edge on macOS uses Apple’s platform passkey store (iCloud Keychain). That store syncs.',
    hint: 'The email is not stored. Expect iCloud Keychain in Edge on this Mac.',
    prompt: 'Complete the Edge / Touch ID passkey prompt.',
    syncedResult: 'On Edge on this Mac, platform passkeys are stored in iCloud Keychain and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Edge stored the passkey in iCloud Keychain',
    storeFailSteps: [
      'Expected. The platform store is iCloud Keychain.',
      'Those passkeys are synced.',
    ],
    noPlatformTitle: 'Edge cannot see a platform authenticator',
    noPlatformSteps: [
      'Set a Mac login password and enable iCloud Keychain.',
      'Retry in Edge.',
    ],
  },
  'macOS:Firefox': {
    hero: 'If Firefox on this Mac creates a platform passkey, macOS stores it in iCloud Keychain. Those passkeys are synced.',
    intro: 'Firefox on macOS uses the OS passkey store when a platform passkey is created. That store is iCloud Keychain and it syncs.',
    hint: 'The email is not stored. Firefox may prompt for iCloud Keychain or a security key.',
    prompt: 'Complete the Firefox / macOS passkey prompt.',
    syncedResult: 'On Firefox on this Mac, platform passkeys are stored in iCloud Keychain and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Firefox used a synced macOS passkey',
    storeFailSteps: [
      'macOS backs platform passkeys with iCloud Keychain.',
      'Those passkeys are synced.',
    ],
    noPlatformTitle: 'Firefox cannot create a platform passkey',
    noPlatformSteps: [
      'Update Firefox. Enable iCloud Keychain if you want the OS store.',
      'Or use a hardware security key.',
    ],
  },
  'iOS:Safari': {
    hero: 'Safari on this iPhone or iPad stores platform passkeys in iCloud Keychain. Those passkeys are synced.',
    intro: 'Safari on iOS uses iCloud Keychain. Face ID / Touch ID only unlocks the key. The store syncs.',
    hint: 'The email is not stored. Expect iCloud Keychain on this device.',
    prompt: 'Complete Face ID, Touch ID, or passcode.',
    syncedResult: 'On Safari on this iPhone or iPad, platform passkeys are stored in iCloud Keychain and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Safari on iOS stored the passkey in iCloud Keychain',
    storeFailSteps: [
      'Expected. iCloud Keychain is the iOS platform passkey store.',
      'Those passkeys are synced.',
    ],
    noPlatformTitle: 'Safari on iOS has no platform authenticator',
    noPlatformSteps: [
      'Set a device passcode.',
      'Enable iCloud Passwords & Keychain on this device.',
    ],
  },
  'iOS:Chrome': {
    hero: 'Chrome on iOS uses Apple’s passkey sheet. Platform passkeys still go to iCloud Keychain and are synced.',
    intro: 'iOS stores Chrome platform passkeys in iCloud Keychain. That store syncs.',
    hint: 'The email is not stored. Chrome on iOS uses Apple’s iCloud Keychain sheet.',
    prompt: 'Complete the iOS passkey sheet (Face ID / passcode).',
    syncedResult: 'On Chrome on this iPhone or iPad, platform passkeys are stored in iCloud Keychain and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Chrome on iOS used iCloud Keychain',
    storeFailSteps: [
      'Expected. iOS hands platform passkeys to iCloud Keychain.',
      'Those passkeys are synced.',
    ],
    noPlatformTitle: 'Chrome on iOS cannot create a platform passkey',
    noPlatformSteps: [
      'Set a device passcode and enable iCloud Keychain.',
      'Retry in Chrome.',
    ],
  },
  'Windows:Chrome': {
    hero: 'Chrome on this Windows PC can store a platform passkey in Windows Hello (this device) or Google Password Manager (synced).',
    intro: 'Chrome on Windows offers Windows Hello and Google Password Manager. Hello stays on this PC. Google Password Manager syncs.',
    hint: 'The email is not stored. In Chrome you may be offered Windows Hello or Google Password Manager.',
    prompt: 'Complete the Chrome passkey prompt.',
    syncedResult: 'Chrome on this Windows PC stored the passkey in a synced store (typically Google Password Manager).',
    hardwareResult: 'Chrome on this Windows PC used Windows Hello. That key stays on this PC.',
    storeFailTitle: 'Chrome stored the passkey in a synced store',
    storeFailSteps: [
      'Chrome on Windows can use Google Password Manager, which syncs.',
      'Choose Windows Hello / this device if you want a key that stays on this PC.',
    ],
    noPlatformTitle: 'Windows Hello is not available in Chrome',
    noPlatformSteps: [
      'Set up a Windows Hello PIN: Settings → Accounts → Sign-in options.',
      'Retry in Chrome.',
    ],
  },
  'Windows:Edge': {
    hero: 'Edge on this Windows PC usually stores platform passkeys in Windows Hello on this device.',
    intro: 'Edge on Windows should offer Windows Hello. That key stays on this PC. A password-manager passkey would sync.',
    hint: 'The email is not stored. Complete Windows Hello when Edge prompts.',
    prompt: 'Complete Windows Hello in Edge.',
    syncedResult: 'Edge stored this passkey in a synced password manager.',
    hardwareResult: 'Edge on this Windows PC used Windows Hello. That key stays on this PC.',
    storeFailTitle: 'Edge used a synced password manager',
    storeFailSteps: [
      'Choose Windows Hello if you want a key that stays on this PC.',
    ],
    noPlatformTitle: 'Windows Hello is not available in Edge',
    noPlatformSteps: [
      'Set up a Windows Hello PIN: Settings → Accounts → Sign-in options.',
      'Retry in Edge.',
    ],
  },
  'Windows:Firefox': {
    hero: 'Firefox on this Windows PC uses the OS WebAuthn stack. Windows Hello stays on this PC; a password-manager store syncs.',
    intro: 'Firefox on Windows should reach Windows Hello. If a password manager handles create, that passkey syncs.',
    hint: 'The email is not stored. Choose Windows Hello if Firefox shows more than one store.',
    prompt: 'Complete Windows Hello in Firefox.',
    syncedResult: 'Firefox stored this passkey in a synced store.',
    hardwareResult: 'Firefox on this Windows PC used Windows Hello. That key stays on this PC.',
    storeFailTitle: 'Firefox used a synced store',
    storeFailSteps: [
      'Choose Windows Hello if you want a key that stays on this PC.',
    ],
    noPlatformTitle: 'Firefox cannot reach Windows Hello',
    noPlatformSteps: [
      'Set up a Windows Hello PIN, then retry in Firefox.',
    ],
  },
  'Android:Chrome': {
    hero: 'Chrome on this Android device stores platform passkeys in Google Password Manager. Those passkeys are synced.',
    intro: 'Android Chrome’s default passkey store is Google Password Manager. The screen lock unlocks the key. The store syncs.',
    hint: 'The email is not stored. Expect Google Password Manager on this phone.',
    prompt: 'Complete the Android screen-lock / passkey prompt.',
    syncedResult: 'On Chrome on this Android device, platform passkeys are stored in Google Password Manager and are synced.',
    hardwareResult: 'This passkey is hardware-bound (typically a security key).',
    storeFailTitle: 'Chrome on Android stored the passkey in Google Password Manager',
    storeFailSteps: [
      'That is the default on Android Chrome.',
      'Those passkeys are synced.',
    ],
    noPlatformTitle: 'Android has no platform authenticator',
    noPlatformSteps: [
      'Set a screen lock (PIN, pattern, or biometrics).',
      'Update Google Play Services, then retry in Chrome.',
    ],
  },
};

const FALLBACK = {
  hero: 'This tool checks whether this browser can complete WebAuthn and whether the passkey store syncs.',
  intro: 'Complete the passkey prompt this browser shows.',
  hint: 'The email is not stored. It only labels the temporary passkey for this test.',
  prompt: 'Complete any system passkey prompt.',
  syncedResult: 'On this platform, the passkey was stored in a synced vault.',
  hardwareResult: 'This passkey is hardware-bound.',
  storeFailTitle: 'Passkey was stored in a synced vault',
  storeFailSteps: [
    'On this platform the passkey store syncs.',
  ],
  noPlatformTitle: 'Platform authenticator unavailable',
  noPlatformSteps: [
    'Configure a device screen lock / biometrics and retry.',
  ],
};

export function copyFor(os, browser) {
  return COPY[platformKey(os, browser)] || FALLBACK;
}

export function successCopy(report) {
  const os = normalizeOs(report.environment?.os);
  const browser = normalizeBrowser(report.environment?.browser);
  const store = report.store_status;
  const vendor = report.store?.vendor || '';
  const hardware = report.mfa20_hardware_bound;
  const who = audience(os, browser);
  const text = copyFor(os, browser);

  if (hardware) {
    return {
      title: text.hardwareResult,
      badge: os === 'Windows' ? 'This device' : 'Hardware-bound',
      card: who,
      body: text.hardwareResult,
    };
  }

  if (store === 'synced_vault' && (vendor === 'Apple' || os === 'macOS' || os === 'iOS')) {
    return {
      title: text.syncedResult,
      badge: 'Synced',
      card: who,
      body: text.syncedResult,
    };
  }

  if (store === 'synced_vault') {
    return {
      title: text.syncedResult,
      badge: 'Synced',
      card: who,
      body: text.syncedResult,
    };
  }

  return {
    title: `${who}: WebAuthn ceremony succeeded`,
    badge: 'Verified',
    card: who,
    body: text.syncedResult,
  };
}
